/* Genre Insights tab — macro→micro acquired-vs-retained view.
   Page padding comes from Layout's .main; the universe title + tab bar sit above
   via TitlesLayout, so this starts straight into controls. Mirrors the Catalog
   tab's structure so the two analyses read consistently. */

.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding-top: var(--space-4);
}

/* ── Controls ─────────────────────────────────────────────────────────────── */
.controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.selects {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Fixed, content-appropriate widths so the selects sit cleanly in a row and the
   trigger doesn't stretch to fill. */
.select {
  width: 180px;
  flex: none;
}

/* Only stack on genuinely narrow viewports, and do it tidily. */
@media (max-width: 720px) {
  .controls {
    flex-direction: column;
    align-items: stretch;
  }
  .selects {
    flex-wrap: wrap;
  }
  .select {
    width: auto;
    flex: 1 1 160px;
  }
}

/* ── Headline takeaway ────────────────────────────────────────────────────── */
.takeaway {
  margin: 0;
  padding: var(--space-3) var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--accent-secondary);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--text-md);
  line-height: 1.5;
}

.takeaway strong {
  color: var(--text-primary);
  font-weight: var(--weight-semi);
}

.takeawayPct {
  color: var(--accent-secondary-text) !important;
  font-variant-numeric: tabular-nums;
}

/* ── Sections ─────────────────────────────────────────────────────────────── */
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.sectionHead {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sectionTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.sectionMeta {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.sectionAccent {
  color: var(--accent-secondary-text);
  font-weight: var(--weight-semi);
}

/* ── Chart card ───────────────────────────────────────────────────────────── */
.chartCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.chartTitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}

.chartTitleIcon {
  display: inline-flex;
  color: var(--text-muted);
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

/* Tabular numerals everywhere a figure appears in the charts. */
.chartCard :global(.recharts-cartesian-axis-tick-value),
.chartCard :global(text) {
  font-variant-numeric: tabular-nums;
}

/* ── States ───────────────────────────────────────────────────────────────── */
.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
}

.errorMsg {
  padding: var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--text-sm);
}

/* ── v2: Insight banner (computed findings) ───────────────────────────────── */
.insightBanner {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--space-3);
}

.insightCard {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--border-strong);
  border-radius: var(--radius-md);
}

/* Accent the left rule + icon by finding kind. */
.insight_acquisition {
  border-left-color: var(--accent-secondary);
}
.insight_retention {
  border-left-color: var(--data-1);
}
.insight_mover {
  border-left-color: var(--accent-primary);
}

.insightIcon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  background: var(--bg-hover);
  color: var(--text-secondary);
}
.insight_acquisition .insightIcon {
  color: var(--accent-secondary-text);
}
.insight_retention .insightIcon {
  color: var(--data-1);
}
.insight_mover .insightIcon {
  color: var(--accent-primary);
}

.insightText {
  margin: 0;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.45;
}

.insightSubject {
  color: var(--text-primary);
}

/* ── v2: Quadrant map ─────────────────────────────────────────────────────── */
.quadrantWrap {
  position: relative;
}

/* Corner labels float over the plot, one per quadrant. Muted so bubbles dominate. */
.quadrantLabel {
  position: absolute;
  z-index: 1;
  padding: 2px 6px;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-muted);
  background: color-mix(in srgb, var(--bg-card) 70%, transparent);
  border-radius: var(--radius-sm);
  pointer-events: none;
}
.q_topRight {
  top: 24px;
  right: 32px;
}
.q_topLeft {
  top: 24px;
  left: 56px;
}
.q_bottomRight {
  bottom: 56px;
  right: 32px;
}
.q_bottomLeft {
  bottom: 56px;
  left: 56px;
}

.quadrantNote {
  margin: var(--space-2) 0 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.4;
}
